/**
 * Health presentation shared by the roster, dashboard, revenue and Customer
 * 360 screens. Everything here renders from `CustomerHealth`; a null score
 * or signal is shown as "No data" in grey — never as a healthy value.
 */
import { HEALTH_SIGNAL_KEYS, type HealthSignalKey } from '@pazportal/types';
import {
  coverageLabel,
  describeSignal,
  formatHealthScore,
  healthSourceLabel,
  HEALTH_STATUS_BAR,
  HEALTH_STATUS_LABEL,
  HEALTH_STATUS_PILL,
  SIGNAL_LABEL,
  SIGNAL_SHORT,
  type CustomerHealth,
} from '../hooks/useAllCustomersWithSignals';

function signalTone(value: number | null): string {
  if (value === null) return 'bg-surface-3 text-text-muted';
  if (value >= 20) return 'bg-success text-white';
  if (value >= 10) return 'bg-warning text-white';
  return 'bg-error text-white';
}

/** Score pill: "82 · Healthy" or "No data". */
export function HealthPill({ health, showLabel = true, className = '' }: { health: CustomerHealth; showLabel?: boolean; className?: string }) {
  const { score, status } = health.signals;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${HEALTH_STATUS_PILL[status]} ${className}`}
      title={`${HEALTH_STATUS_LABEL[status]} · ${coverageLabel(health.signals.coverage)} · ${healthSourceLabel(health)}`}
    >
      {score === null ? HEALTH_STATUS_LABEL.unknown : formatHealthScore(score)}
      {showLabel && score !== null && <span className="font-normal opacity-80">· {HEALTH_STATUS_LABEL[status]}</span>}
    </span>
  );
}

/** Thin progress bar under a score; empty when the score is unknown. */
export function HealthBar({ health, className = '' }: { health: CustomerHealth; className?: string }) {
  const { score, status } = health.signals;
  return (
    <div className={`h-1 w-16 bg-surface-3 rounded-full overflow-hidden ${className}`} aria-hidden="true">
      {score !== null && (
        <div className={`h-full rounded-full ${HEALTH_STATUS_BAR[status]}`} style={{ width: `${score}%` }} />
      )}
    </div>
  );
}

/** Five compact chips (T S U R M) with a tooltip describing each input. */
export function SignalChips({ health }: { health: CustomerHealth }) {
  const signals = health.signals;
  return (
    <ul className="flex gap-0.5 justify-center" aria-label="Health signals">
      {HEALTH_SIGNAL_KEYS.map((key: HealthSignalKey) => {
        const value = signals[key];
        const detail = describeSignal(key, health);
        return (
          <li
            key={key}
            title={`${SIGNAL_LABEL[key]}: ${value === null ? 'no data' : `${value}/20`} — ${detail}`}
            className={`inline-block w-4 h-4 rounded-sm text-[9px] font-bold leading-4 text-center ${signalTone(value)}`}
          >
            <span aria-hidden="true">{SIGNAL_SHORT[key]}</span>
            <span className="sr-only">{`${SIGNAL_LABEL[key]}: ${value === null ? 'no data' : `${value} of 20`}, ${detail}`}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Row-per-signal breakdown for the Customer 360 side panel. */
export function SignalList({ health }: { health: CustomerHealth }) {
  const signals = health.signals;
  return (
    <ul className="space-y-1.5">
      {HEALTH_SIGNAL_KEYS.map((key) => {
        const value = signals[key];
        return (
          <li key={key} className="flex items-start gap-2">
            <span className={`mt-0.5 w-3 h-3 rounded-sm shrink-0 ${signalTone(value)}`} aria-hidden="true" />
            <div className="min-w-0">
              <span className="text-xs font-medium text-text">{SIGNAL_LABEL[key]}</span>
              <span className="text-[10px] text-text-muted ml-1">
                {value === null ? 'No data' : `${value}/20`} · {describeSignal(key, health)}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** "Computed now · 3 of 5 signals" footer line. */
export function HealthProvenance({ health, className = '' }: { health: CustomerHealth; className?: string }) {
  return (
    <p className={`text-[11px] text-text-muted ${className}`}>
      {healthSourceLabel(health)} · {coverageLabel(health.signals.coverage)}
    </p>
  );
}
